import React, { useEffect, useState } from 'react';
import { Button, Message, Table, Pagination, FormControl, Icon } from 'tinper-bee';
import * as util from '../../utils';
import './SkillDetail.less';
import Layout from '../Layout';
import marked from 'marked';

interface SkillDetailData {
  id: string;
  name: string;
  latestVersion?: string;
  version?: string;
  info?: string;
  description?: string;
  skillDescription?: string;
  author?: string;
  download?: number;
  visitVolume?: number;
  size?: number;
  stars?: number;
  installs?: number;
  tags?: string[];
  latestTime?: string;
  readmeFile?: string;
  readme?: string;
  nexus?: string;
  img?: string;
  skillRecommend?: string;
  skillRecommendRemark?: string;
  [key: string]: any;
}

interface VersionHistory {
  version: string;
  CDN: string;
  creatTime: string;
}

const SkillDetail: React.FC<any> = (props) => {
  const [skill, setSkill] = useState<SkillDetailData | null>(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('files');
  const [readmeContent, setReadmeContent] = useState('');
  const [historyList, setHistoryList] = useState<VersionHistory[]>([]);
  const [paginationInfo, setPaginationInfo] = useState<any>({});
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [searchVersion, setSearchVersion] = useState('');
  // begin add for 登录状态控制: Download zip按钮仅登录用户可见 at 20260409
  const [isLoggedIn, setIsLoggedIn] = useState(!!localStorage.getItem('user') && localStorage.getItem('user') !== 'undefined');
  // end add for 登录状态控制: Download zip按钮仅登录用户可见 at 20260409

  const historyColumns = [
    { title: "版本号", dataIndex: "version", key: "version", width: 275 },
    { 
      title: "下载源码", 
      dataIndex: "CDN", 
      key: "CDN", 
      width: 200, 
      render: (text: string) => <a href={text} download><Icon type="uf-cloud-down" /></a>
    },
    { title: "更新时间", dataIndex: "creatTime", key: "creatTime", width: 175 }
  ];

  const pageSizes = [5, 10, 15, 20];

  const recommendClass = skill?.skillRecommend === '交付龙' ? 'delivery' : skill?.skillRecommend === '价值龙' ? 'value' : 'efficiency';

  useEffect(() => {
    fetchSkillDetail();
  }, []);

  useEffect(() => {
    if (activeTab === 'versions' && skill) {
      getHistoryList();
    }
  }, [activeTab, page, pageSize]);

  // 页面加载时也获取一次版本历史，用于下载按钮
  useEffect(() => {
    if (skill && historyList.length === 0) {
      getHistoryList();
    }
  }, [skill]);

  const fetchSkillDetail = async () => {
    setLoading(true);
    try {
      const { name } = props.match.params;
      const response = await util.get('/package/packageDetail', { packagename: name });
      if (response && response.status === 200 && response.data) {
        setSkill(response.data);
        
        // 获取 README 内容
        if (response.data.readmeFile && response.data.id) {
          const readme = await util.get('/package/readMeFile', { id: response.data.id });
          if (readme) {
            setReadmeContent(marked(readme));
          }
        }
      } else {
        Message.create({ content: '技能不存在', color: 'danger' });
      }
    } catch (error) {
      console.error('Failed to fetch skill detail:', error);
      Message.create({ content: '获取技能详情失败', color: 'danger' });
    } finally {
      setLoading(false);
    }
  };

  const getHistoryList = async () => {
    if (!skill) return;
    
    try {
      const res = await util.get('/package/packageHistory', {
        packagename: skill.name,
        pageNumber: page,
        version: searchVersion,
        pageSize,
        auth: 1
      });
      
      if (res.status === 200) {
        setHistoryList(res.data.list || []);
        setPaginationInfo(res.data.pageInfo || {});
      }
    } catch (error) {
      console.error('Failed to fetch history:', error);
    }
  };

  const handleSync = async () => {
    if (!skill) return;
    
    Message.create({ content: "组件同步任务已开启，请稍后！", color: 'success' });
    try {
      const result = await util.post('/package/sync', { id: skill.id, name: skill.name });
      if (result.status === 200) {
        Message.create({ content: "组件同步成功！", color: 'success' });
        fetchSkillDetail();
        if (activeTab === 'versions') {
          getHistoryList();
        }
      } else {
        Message.create({ content: "组件同步任务未返回结果，请过一会刷新查看同步结果！", color: 'danger' });
      }
    } catch (error) {
      Message.create({ content: "同步失败", color: 'danger' });
    }
  };

  const handleDownload = async () => {
    if (historyList.length > 0 && historyList[0].CDN) {
      // 使用最新版本的 CDN 下载链接
      window.open(historyList[0].CDN, '_blank');

      // 上报下载统计
      try {
        const packageInfo = { name: skill?.name, version: historyList[0].version };
        await util.post('/package/addDownloadNum', {
          installPackMap: JSON.stringify([packageInfo])
        });
        await util.get('/package/packageDownloadDetail', {
          package_name: JSON.stringify([packageInfo])
        });
      } catch (e) {
        // 忽略统计上报错误
      }
    } else {
      Message.create({ content: '暂无可下载的版本，请稍后再试', color: 'warning' });
    }
  };

  const handleSearchVersion = () => {
    setPage(1);
    getHistoryList();
  };

  const pageChange = (newPage: number) => {
    setPage(newPage);
  };

  const dataNumSelect = (index: number) => {
    setPageSize(pageSizes[index]);
    setPage(1);
  };

  const formatNumber = (num: number) => {
    if (!num) return '0';
    if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
    if (num >= 1000) return `${(num / 1000).toFixed(1)}k`;
    return num.toString();
  };

  if (loading) {
    return (
      <div className="skill-detail-page">
        <div className="loading-container">加载中...</div>
      </div>
    );
  }

  if (!skill) {
    return (
      <div className="skill-detail-page">
        <div className="error-container">技能不存在</div>
      </div>
    );
  }

  return (
    <div className="skill-detail-page">
      <div className="skill-detail-container wrap">
        <div className={`detail-header${skill.skillRecommend ? ' has-recommend' : ''}`}>
          <div className="header-main">
            <div className="skill-title-row">
              <h1 className="skill-title">{skill.name}</h1>
              {skill.skillRecommend && (
                <span
                  className={`detail-recommend-badge ${recommendClass}`}
                  title={skill.skillRecommendRemark || skill.skillRecommend}
                >
                  <span className="detail-recommend-label">{skill.skillRecommend}</span>
                </span>
              )}
            </div>
            <p className="skill-description">
              {skill.skillDescription || skill.info || skill.description || '暂无描述'}
            </p>

            <div className="skill-stats">
              <div className="stat-item">
                <span className="stat-label">{formatNumber(skill.visitVolume || 0)} 访问量</span>
              </div>
              <div className="stat-item">
                <span className="stat-label">{formatNumber(skill.download || 0)} 下载量</span>
              </div>
              <div className="stat-item">
                <span className="stat-label">{formatNumber(skill.skill_active_count || 0)} 激活量</span>
              </div>
            </div>

            <div className="skill-meta">
              <span className="meta-item">
                by <span className="meta-author">@{skill.creatorName || 'anonymous'}</span>
              </span>
            </div>
          </div>

          <div className="header-sidebar">
            <div className="version-box">
              <div className="version-label">CURRENT VERSION</div>
              <div className="version-value">v{skill.latestVersion || skill.version || '1.0.0'}</div>
            </div>
            {/* begin add for 登录状态控制: Download zip按钮仅登录用户可见 at 20260409 */}
            {isLoggedIn && (
              <Button className="download-btn" onClick={handleDownload}>
                Download zip
              </Button>
            )}
            {/* end add for 登录状态控制: Download zip按钮仅登录用户可见 at 20260409 */}
          </div>
        </div>

        <div className="detail-tabs">
          <div className="tabs-header">
            <button 
              className={`tab-btn ${activeTab === 'files' ? 'active' : ''}`}
              onClick={() => setActiveTab('files')}
            >
              Files
            </button>
            <button 
              className={`tab-btn ${activeTab === 'versions' ? 'active' : ''}`}
              onClick={() => setActiveTab('versions')}
            >
              Versions
            </button>
          </div>

          <div className="tabs-content">
            {activeTab === 'files' && (
              <div className="tab-panel">
                <h2 className="panel-title">SKILL.md</h2>
                <div className="skill-readme">
                  {readmeContent ? (
                    <article className="markdown-body">
                      <div dangerouslySetInnerHTML={{ __html: readmeContent }} />
                    </article>
                  ) : (
                    <div className="empty-readme">
                      <p className="readme-note">暂无详细文档</p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {activeTab === 'versions' && (
              <div className="tab-panel">
                <div className="version-search">
                  <div className="search-row">
                    <span className="search-label">版本号：</span>
                    <FormControl
                      className="search-input"
                      value={searchVersion}
                      onChange={(e: any) => setSearchVersion(e)}
                      placeholder="输入版本号搜索"
                    />
                    <Button className="search-btn" onClick={handleSearchVersion}>
                      查询
                    </Button>
                    <Icon 
                      className="sync-icon" 
                      onClick={handleSync} 
                      type="uf-sync-c-o" 
                      title="同步最新版本"
                    />
                  </div>
                </div>

                {historyList.length > 0 ? (
                  <>
                    <Table
                      className="version-table"
                      data={historyList}
                      columns={historyColumns}
                    />
                    {paginationInfo.count > pageSize && (
                      <Pagination
                        prev
                        next
                        maxButtons={5}
                        boundaryLinks
                        activePage={page}
                        onSelect={pageChange}
                        onDataNumSelect={dataNumSelect}
                        showJump={true}
                        dataNum={3}
                        total={paginationInfo.count}
                      />
                    )}
                  </>
                ) : (
                  <div className="empty-message">暂无版本历史</div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Layout(SkillDetail, 'skill-detail-page-1');
